<template>
  <div class="page-container pending-reverse">
    <div class="main">
      <page :url="url" :form="form" @reset="reset">
        <template slot="tableSearch">
          <el-form-item v-if="ROLE_TYPE.isManager() || ROLE_TYPE.isAgent()"
                        :label="$t('text.merchant')">
            <el-select v-model="form.appId"
                       filterable
                       clearable
                       :placeholder="$t('message.chooseOne')"
                       popper-class="eloption">
              <el-option :label="$t('option.all')" value=""/>
              <el-option v-for="merchant in merchantList"
                         :label="merchant.value + '-' + merchant.name"
                         :key="merchant.value"
                         :value="merchant.value"/>
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('text.merchantOrderId')">
            <el-input v-model="form.merchantOrderId" style="width: 240px" maxlength="50"/>
          </el-form-item>
          <el-form-item :label="$t('text.platformOrderId')">
            <el-input v-model="form.orderId" style="width: 240px" maxlength="30"/>
          </el-form-item>
          <el-form-item :label="$t('text.paymentType')">
            <el-select v-model="form.payType" :placeholder="$t('message.chooseOne')">
              <el-option :label="$t('option.all')" value=""/>
              <el-option :label="$t('text.collection')" value="110"/>
              <el-option :label="$t('text.disbursement')" value="120"/>
            </el-select>
          </el-form-item>
          <el-form-item v-if="ROLE_TYPE.isManager()" :label="$t('text.paymentChannel')">
            <el-select v-model="form.channelId" filterable clearable :placeholder="$t('message.chooseOne')">
              <el-option :label="$t('option.all')" value=""/>
              <el-option v-for="item in payOption"
                         :key="item.value || item.channelId"
                         :label="item.name || item.channelName"
                         :value="item.value || item.channelId"/>
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('text.pendingReverseStatus')">
            <el-select v-model="statusFilter" :placeholder="$t('message.chooseOne')" @change="onStatusFilterChange">
              <el-option :label="$t('pendingReverseStatus.PENDING')" value="PENDING"/>
              <el-option :label="$t('pendingReverseStatus.REVERSED')" value="REVERSED"/>
              <el-option :label="$t('pendingReverseStatus.CLOSED')" value="CLOSED"/>
              <el-option :label="$t('option.all')" value="ALL"/>
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('text.reverseDirection')">
            <el-select v-model="direction" :placeholder="$t('message.chooseOne')" @change="onDirectionChange">
              <el-option :label="$t('option.all')" value=""/>
              <el-option :label="$t('text.failedToSuccess')" value="02_01"/>
              <el-option :label="$t('text.successToFailed')" value="01_02"/>
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('text.enqueueTime')">
            <el-date-picker
                v-model="enqueueTimeDate"
                type="daterange"
                format="yyyy-MM-dd"
                value-format="yyyyMMdd"
                :range-separator="$t('text.to')"
                :start-placeholder="$t('text.startDate')"
                :end-placeholder="$t('text.endDate')"
                @change="enqueueTimeChange"
            />
          </el-form-item>
        </template>

        <template slot="buttonArea">
          <el-button type="primary"
                     :loading="exporting"
                     :disabled="exporting"
                     @click="exportDetail"
                     size="small">
            {{ $t('text.export') }}
          </el-button>
        </template>

        <template slot="tableColumn">
          <el-table-column prop="merchantName" width="160px" :label="$t('text.merchant')" align="center"/>
          <el-table-column prop="payType" width="90px" :label="$t('text.paymentType')" align="center">
            <template slot-scope="scope">
              {{ scope.row.payType === '110' ? $t('text.collection') : $t('text.disbursement') }}
            </template>
          </el-table-column>
          <el-table-column prop="status" width="110px" :label="$t('text.pendingReverseStatus')" align="center">
            <template slot-scope="scope">
              <el-tag size="medium" :type="statusTagType(scope.row.status)">
                {{ $t('pendingReverseStatus.' + scope.row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column width="160px" :label="$t('text.reverseDirection')" align="center">
            <template slot-scope="scope">
              {{ formatDirection(scope.row) }}
            </template>
          </el-table-column>
          <el-table-column prop="signalSource" width="110px" :label="$t('text.signalSource')" align="center"/>
          <el-table-column prop="signalRemark" width="180px" :label="$t('text.signalRemark')" align="center"
                           show-overflow-tooltip/>
          <el-table-column v-if="ROLE_TYPE.isManager()"
                           prop="channelName"
                           width="140px"
                           :label="$t('text.paymentChannel')"
                           align="center"/>
          <el-table-column prop="merchantOrderId" width="160px" :label="$t('text.merchantOrderId')" align="center"/>
          <el-table-column prop="orderId" width="160px" :label="$t('text.platformOrderId')" align="center"/>
          <el-table-column prop="orderAmount" width="110px" :label="$t('text.amount')" align="center">
            <template slot-scope="scope">
              {{ formatOrderAmount(scope.row.orderAmount) }}
            </template>
          </el-table-column>
          <el-table-column prop="createTimeL" width="160px" :label="$t('text.enqueueTime')" align="center">
            <template slot-scope="scope">
              {{ formatTime(scope.row.createTimeL) }}
            </template>
          </el-table-column>
          <el-table-column prop="reversedBy" width="120px" :label="$t('text.reversedBy')" align="center"/>
          <el-table-column :label="$t('text.actions')" width="220px" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button v-if="canReverseToSuccess(scope.row)"
                         type="danger"
                         size="mini"
                         plain
                         @click="confirmReverse(scope.row, 'success')">
                {{ $t('text.reverse') }}
              </el-button>
              <el-button v-else-if="canReverseToFailed(scope.row)"
                         type="danger"
                         size="mini"
                         plain
                         @click="confirmReverse(scope.row, 'failed')">
                {{ $t('text.reverse') }}
              </el-button>
              <el-button v-if="canClose(scope.row)"
                         type="info"
                         size="mini"
                         plain
                         @click="confirmClose(scope.row)">
                {{ $t('text.close') }}
              </el-button>
              <span v-if="scope.row.status === 'REVERSED' || scope.row.status === 'CLOSED'">—</span>
              <span v-else-if="scope.row.status === 'PENDING'
                         && !canReverseToSuccess(scope.row)
                         && !canReverseToFailed(scope.row)
                         && !canClose(scope.row)"
                    class="muted">{{ $t('text.viewOnly') }}</span>
            </template>
          </el-table-column>
        </template>
      </page>
    </div>
  </div>
</template>

<script>
import {ROLE_TYPE} from "@/utils/Constant";
import Page from "@/components/Page.vue";
import moment from "moment";

export default {
  name: "PendingReverse",
  components: {page: Page},
  computed: {
    ROLE_TYPE() {
      return ROLE_TYPE;
    }
  },
  data() {
    return {
      url: "/pendingReverse/selectList",
      direction: "",
      statusFilter: "PENDING",
      enqueueTimeDate: null,
      exporting: false,
      merchantList: [],
      payOption: [],
      form: {
        appId: "",
        merchantOrderId: "",
        orderId: "",
        payType: "",
        channelId: "",
        status: "PENDING",
        fromStatus: "",
        toStatus: "",
        startTimeL: "",
        endTimeL: ""
      }
    };
  },
  methods: {
    reset() {
      this.direction = "";
      this.statusFilter = "PENDING";
      this.enqueueTimeDate = null;
      this.form.appId = "";
      this.form.merchantOrderId = "";
      this.form.orderId = "";
      this.form.payType = "";
      this.form.channelId = "";
      this.form.fromStatus = "";
      this.form.toStatus = "";
      this.form.startTimeL = "";
      this.form.endTimeL = "";
      this.onStatusFilterChange("PENDING");
    },
    enqueueTimeChange(e) {
      if (e) {
        this.form.startTimeL = moment(e[0], "YYYYMMDD").startOf("day").valueOf();
        this.form.endTimeL = moment(e[1], "YYYYMMDD").endOf("day").valueOf();
      } else {
        this.form.startTimeL = "";
        this.form.endTimeL = "";
      }
    },
    onStatusFilterChange(val) {
      if (val === "ALL") {
        // 不传 status：后端默认 PENDING + REVERSED（不含 CLOSED）
        this.form.status = "";
      } else if (val === "REVERSED") {
        this.form.status = "REVERSED";
      } else if (val === "CLOSED") {
        this.form.status = "CLOSED";
      } else {
        this.form.status = "PENDING";
      }
    },
    onDirectionChange(val) {
      if (!val) {
        this.form.fromStatus = "";
        this.form.toStatus = "";
        return;
      }
      const parts = val.split("_");
      this.form.fromStatus = parts[0];
      this.form.toStatus = parts[1];
    },
    formatDirection(row) {
      const from = this.$formatter.formatOrderStatus(null, null, row.fromStatus, null);
      const to = this.$formatter.formatOrderStatus(null, null, row.toStatus, null);
      return from + " → " + to;
    },
    formatTime(ts) {
      if (!ts) {
        return "";
      }
      return moment(Number(ts)).format("YYYY-MM-DD HH:mm:ss");
    },
    formatOrderAmount(orderAmount) {
      if (typeof orderAmount === "number") {
        return orderAmount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      }
      return orderAmount;
    },
    statusTagType(status) {
      if (status === "PENDING") {
        return "warning";
      }
      if (status === "CLOSED") {
        return "info";
      }
      return "";
    },
    // 代收仅失败→成功
    canReverseToSuccess(row) {
      return row.status === "PENDING"
          && row.payType === "110"
          && row.fromStatus === "02"
          && row.toStatus === "01";
    },
    // 代付仅成功→失败
    canReverseToFailed(row) {
      return row.status === "PENDING"
          && row.payType === "120"
          && row.fromStatus === "01"
          && row.toStatus === "02";
    },
    canClose(row) {
      return row.status === "PENDING";
    },
    confirmReverse(row, direction) {
      const tip = direction === "success"
          ? this.$t("message.confirmReverseToSuccess")
          : this.$t("message.confirmReverseToFailed");
      this.$confirm(tip, this.$t("text.prompt"), {
        confirmButtonText: this.$t("text.confirm"),
        cancelButtonText: this.$t("text.cancel"),
        type: "warning"
      }).then(() => {
        this.axios.post("/pendingReverse/reverse", {orderId: row.orderId})
            .then((res) => {
              if (res.code === 200) {
                this.$notify({
                  title: this.$t("text.prompt"),
                  message: res.msg,
                  type: "success"
                });
                this.$bus.$emit("refresh");
              } else {
                this.$notify.error({
                  title: this.$t("text.prompt"),
                  message: res.msg
                });
              }
            });
      }).catch(() => {
      });
    },
    exportDetail() {
      this.exporting = true;
      this.axios
          .get("/pendingReverse/export", {
            params: this.form,
            responseType: "blob",
            timeout: 30 * 60 * 1000
          })
          .then((res) => {
            if (res.data) {
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const link = document.createElement("a");
              link.href = url;
              link.setAttribute("download", this.$t("text.pendingReverse.xlsx"));
              document.body.appendChild(link);
              link.click();
            } else if (res.text) {
              res.text()
                  .then(JSON.parse)
                  .then((body) => {
                    this.$message({
                      message: body.msg,
                      type: body.code === 200 ? "success" : "error"
                    });
                  });
            }
          })
          .catch(() => {
            this.$notify.error({
              title: this.$t("text.prompt"),
              message: this.$t("message.exportFailed")
            });
          })
          .finally(() => {
            this.exporting = false;
          });
    },
    confirmClose(row) {
      this.$confirm(this.$t("message.confirmClosePendingReverse"), this.$t("text.prompt"), {
        confirmButtonText: this.$t("text.confirm"),
        cancelButtonText: this.$t("text.cancel"),
        type: "warning"
      }).then(() => {
        this.axios.post("/pendingReverse/close", {orderId: row.orderId})
            .then((res) => {
              if (res.code === 200) {
                this.$notify({
                  title: this.$t("text.prompt"),
                  message: res.msg,
                  type: "success"
                });
                this.$bus.$emit("refresh");
              } else {
                this.$notify.error({
                  title: this.$t("text.prompt"),
                  message: res.msg
                });
              }
            });
      }).catch(() => {
      });
    }
  },
  created() {
    this.$axios.get("/merchant/selectListForOption", {params: {}}).then((res) => {
      this.merchantList = res.data || [];
    });
    this.$axios.get("/routeChannelInfo/selectByCondition", {params: {}}).then((res) => {
      this.payOption = res.data || [];
    });
  }
};
</script>

<style scoped>
.muted {
  color: #909399;
  font-size: 12px;
}
</style>
